import { useMemo, useState } from 'react'
import cloneDeep from 'lodash/cloneDeep'

import Breakpoints from 'common/breakpoints'
import AvatarList from 'common/components/AvatarList'
import ButtonWithTooltip from 'common/components/ButtonWithTooltip'
import { Column } from 'common/components/table'
import BaseCell from 'common/components/table/cells/Base'
import TableWidget from 'common/components/widget/tableWidget'
import { formatPastDate } from 'common/helpers/date'
import { useAppSelector } from 'common/hooks/redux'
import { useWindowDimensions } from 'common/hooks/useWindowDimensions'
import { Add as AddIcon } from 'common/icons'
import { AdvancedLanguageRule } from 'common/interfaces/brandVoices'
import useGetBrandVoiceDetailsQuery from 'features/admin/brandVoice/api/queries/useGetBrandVoiceDetailsQuery'

import EditAdvancedLanguageRulesDrawer from './manageAdvancedLanguageRulesDrawer/EditAdvancedLanguageRulesDrawer'
import AdvancedLanguageRulesActions from './AdvancedLanguageRulesActions'
import AddAdvancedLanguageRulesDrawer from './manageAdvancedLanguageRulesDrawer'

const MAX_RULE_COUNT = 10

const AdvancedLanguageRules = () => {
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState<boolean>(false)

  const { screenWidth } = useWindowDimensions()
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const selectedBrandVoiceId = useAppSelector(
    (state) => state.styleGuide.selectedBrandVoiceId
  )

  const { data: brandVoice, isLoading: isLoadingBrandVoiceDetails } =
    useGetBrandVoiceDetailsQuery(accountId, selectedBrandVoiceId || '')

  const advancedLanguageRules = brandVoice?.languageRules || []

  const [selectedAdvancedLanguageRule, setSelectedAdvancedLanguageRule] =
    useState<{
      id: string | undefined
      isVisible: boolean
    }>({
      id: advancedLanguageRules[0]?.id,
      isVisible: false,
    })

  const columns: Column<AdvancedLanguageRule>[] = useMemo(
    () => [
      {
        Header: 'Instruction',
        accessor: 'rule',
      },
      {
        Header: 'Last modified',
        accessor: 'lastModified',
        Cell: ({ value }) => <BaseCell>{formatPastDate(value)}</BaseCell>,
        width: 60,
        align: 'left',
      },
      {
        Header: 'Created',
        accessor: 'created',
        Cell: ({ value }) => <BaseCell>{formatPastDate(value)}</BaseCell>,
        width: 60,
      },
      {
        Header: 'User',
        accessor: 'user',
        width: 20,
        disableSortBy: true,
        align: 'right',
        Cell: ({ value }) => {
          const initials = value
            ?.split(' ')
            .map((name) => name[0])
            .join('')

          return (
            <BaseCell className="flex justify-center">
              <AvatarList userNames={[initials]} />
            </BaseCell>
          )
        },
      },
      {
        Header: '',
        accessor: 'id',
        width: 25,
        disableSortBy: true,
        align: 'right',
        Cell: ({ row }) => {
          return (
            <BaseCell className="invisible group-hover:visible">
              <AdvancedLanguageRulesActions
                rule={row.original}
                onEdit={() => {
                  setSelectedAdvancedLanguageRule({
                    id: row.original.id,
                    isVisible: true,
                  })
                }}
              />
            </BaseCell>
          )
        },
      },
    ],
    []
  )

  const isAddButtonDisabled = advancedLanguageRules.length >= MAX_RULE_COUNT
  const selectedRule = cloneDeep(
    advancedLanguageRules?.find(
      ({ id }) => selectedAdvancedLanguageRule.id === id
    )!
  )

  return (
    <>
      <AddAdvancedLanguageRulesDrawer
        isOpen={isAddDrawerOpen}
        onOpenChange={setIsAddDrawerOpen}
      />
      <EditAdvancedLanguageRulesDrawer
        initialValues={selectedRule}
        isOpen={selectedAdvancedLanguageRule.isVisible}
        onOpenChange={() => {
          setSelectedAdvancedLanguageRule(({ id }) => {
            return {
              id,
              isVisible: false,
            }
          })
        }}
      />
      <TableWidget.Widget
        data-testid="advanced-language-rules-widget"
        className="mt-6"
        data={advancedLanguageRules}
        columns={columns}
        isLoading={isLoadingBrandVoiceDetails}
        firstUseText="You don't have any instructions"
        initialState={{
          sortBy: [
            {
              id: 'lastModified',
              desc: true,
            },
          ],
        }}
      >
        <TableWidget.Header
          title="Brand voice instructions"
          subtitle="Add up to 10 instructions with clear examples to guide language generation that remains consistent, precise, and aligned with your brand voice."
        >
          <ButtonWithTooltip
            className="mr-4"
            variant="primary"
            tooltip={isAddButtonDisabled}
            tooltipText="Instructions limit reached"
            data-testid="add-advanced-language-rule-button"
            prefixIcon={<AddIcon size={3} isDefaultColor={false} />}
            onClick={() => {
              setIsAddDrawerOpen(true)
            }}
            disabled={isAddButtonDisabled}
          >
            {screenWidth < Breakpoints.sm ? undefined : 'Instruction'}
          </ButtonWithTooltip>
        </TableWidget.Header>
      </TableWidget.Widget>
    </>
  )
}

export default AdvancedLanguageRules
